/**
 * BB-077 · Bulk Upload Wizard — "Upload Everything"
 *
 * Full-screen modal with four steps:
 *   1. Drag-and-drop file upload  (upload → server)
 *   2. AI categorization review   (process → present results)
 *   3. AI data extraction review  (extract → structured suggestions) [Phase 2]
 *   4. Confirm & import           (confirm → create Document + structured records)
 *
 * Phase 2 adds step 3: after categorization, AI reads file content and suggests
 * structured records (financial entries, contacts, permit metadata, photo links).
 * Confident values are saved on import. Ambiguous ones stay as cards.
 *
 * Entry points: Documents tab "Bulk Upload" button, Dashboard card,
 * project header floating action.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import {
  Upload, X, FileText, Check, ChevronRight, ChevronDown, ChevronUp,
  AlertCircle, Edit3, Loader2, CheckCircle2, Image as ImageIcon,
  File, Ruler, Shield, Receipt, FileCheck, FolderOpen, Camera,
  Sparkles, ArrowLeft, RotateCcw, Smartphone, DollarSign, UserPlus,
  FileSearch, Link2, SkipForward, CheckCheck,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { buildConfirmedFiles } from "@/lib/bulkConfirm";
import {
  suggestionCopy,
  cardActionCopy,
  cardNeedsChoice,
  editableKeys,
  reviewFromConfirm,
} from "../lib/bulkConfirm";
import AssistantConfirmCard from "./AssistantConfirmCard";
import { BULK_BATCH_SIZE, dedupeQueued, splitIntoBatches } from "../lib/bulkBatches";
import {
  clearBulkPending,
  pendingFileCount,
  readBulkPending,
  writeBulkPending,
} from "../lib/bulkPending";
import {
  collectDroppedFiles,
  fileDisplayName,
  partitionUploadFiles,
} from "../lib/collectDroppedFiles";
import PhoneCaptureModal from "./PhoneCaptureModal";
import UploadPreview from "./UploadPreview";

// ─── Category config (mirrors DOC_CATEGORY_CONFIG) ────────────────────────

const CATEGORIES = [
  { value: "blueprints",  label: "Blueprints",         icon: Ruler,       bg: "bg-blue-100",    fg: "text-blue-600" },
  { value: "permits",     label: "Permits",            icon: Shield,      bg: "bg-amber-100",   fg: "text-amber-600" },
  { value: "contracts",   label: "Contracts",          icon: FileText,    bg: "bg-purple-100",  fg: "text-purple-600" },
  { value: "quotes",      label: "Quotes & Estimates", icon: FileCheck,   bg: "bg-green-100",   fg: "text-green-600" },
  { value: "invoices",    label: "Invoices & Receipts",icon: Receipt,     bg: "bg-red-100",     fg: "text-red-600" },
  { value: "warranties",  label: "Warranties",         icon: CheckCircle2,bg: "bg-emerald-100", fg: "text-emerald-600" },
  { value: "photos",      label: "Photos",             icon: Camera,      bg: "bg-indigo-100",  fg: "text-indigo-600" },
  { value: "other",       label: "Other",              icon: FolderOpen,  bg: "bg-slate-100",   fg: "text-slate-500" },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

const CONFIDENCE_CONFIG = {
  high:   { icon: CheckCircle2, color: "text-green-500", label: "High confidence" },
  medium: { icon: AlertCircle,  color: "text-amber-500", label: "Medium confidence" },
  low:    { icon: AlertCircle,  color: "text-red-400",   label: "Low confidence" },
};

// ─── Extraction type config ─────────────────────────────────────────────

const EXTRACTION_TYPE_CONFIG = {
  financial_entry:      { icon: DollarSign, label: "Financial Entry",  bg: "bg-red-50",     border: "border-red-200",    fg: "text-red-600",    iconBg: "bg-red-100" },
  contact:              { icon: UserPlus,   label: "Contact",          bg: "bg-purple-50",  border: "border-purple-200", fg: "text-purple-600", iconBg: "bg-purple-100" },
  contact_and_financial:{ icon: FileCheck,  label: "Quote Details",    bg: "bg-green-50",   border: "border-green-200",  fg: "text-green-600",  iconBg: "bg-green-100" },
  permit_metadata:      { icon: Shield,     label: "Permit Details",   bg: "bg-amber-50",   border: "border-amber-200",  fg: "text-amber-600",  iconBg: "bg-amber-100" },
  warranty_metadata:    { icon: CheckCircle2,label: "Warranty Details", bg: "bg-emerald-50", border: "border-emerald-200",fg: "text-emerald-600",iconBg: "bg-emerald-100" },
  photo_link:           { icon: Link2,      label: "Photo Link",       bg: "bg-indigo-50",  border: "border-indigo-200", fg: "text-indigo-600", iconBg: "bg-indigo-100" },
};

// ─── Helper: format file size ──────────────────────────────────────────────

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function groupSessionFiles(files) {
  const groups = {};
  const categoryOrder = ["other", ...CATEGORIES.map((c) => c.value).filter((value) => value !== "other")];
  (files || []).forEach((file) => {
    const cat = file.user_category || file.ai_category || "other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(file);
  });
  const sorted = {};
  categoryOrder.forEach((cat) => {
    if (groups[cat]) sorted[cat] = groups[cat];
  });
  return sorted;
}

function stampBatch(files, sessionId, batchNumber) {
  return (files || []).map((file) => ({
    ...file,
    session_id: sessionId,
    batch_number: batchNumber,
  }));
}

const BATCH_READ_MS = 8 * 60 * 1000;
const EXTRACT_AT_ONCE = 2;

async function loadBatchSuggestions(projectId, sessionId, onWait, options) {
  const opts = options || {};
  if (!opts.skipPost) {
    const categories = opts.categories || {};
    const body = Object.keys(categories).length ? { categories } : {};
    const extractRes = await api.post(
      `/projects/${projectId}/bulk-upload/${sessionId}/extract`,
      body,
      { timeout: 270000 },
    );
    if (opts.cancelledRef && opts.cancelledRef.current) {
      return { ok: false, cancelled: true, suggestions: [] };
    }
    const status = extractRes.data.status;
    if (status === "extract_failed") return { ok: false, waited: false, suggestions: [] };
    if (status !== "extracting") {
      return {
        ok: true,
        waited: false,
        suggestions: (extractRes.data.suggestions || []).map((row) => ({ ...row, status: "pending" })),
      };
    }
    if (onWait) onWait();
  }
  const started = Date.now();
  let session = null;
  while (Date.now() - started < BATCH_READ_MS) {
    await new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (opts.timersRef) opts.timersRef.current.delete(timer);
        resolve();
      }, 5000);
      if (opts.timersRef) opts.timersRef.current.add(timer);
    });
    if (opts.cancelledRef && opts.cancelledRef.current) {
      return { ok: false, cancelled: true, suggestions: [] };
    }
    const res = await api.get(`/projects/${projectId}/bulk-upload/${sessionId}`);
    if (opts.cancelledRef && opts.cancelledRef.current) {
      return { ok: false, cancelled: true, suggestions: [] };
    }
    session = res.data;
    if (session.status === "extracted" || session.status === "extract_failed") break;
  }
  if (opts.cancelledRef && opts.cancelledRef.current) {
    return { ok: false, cancelled: true, suggestions: [] };
  }
  if (!session || session.status !== "extracted") return { ok: false, waited: true, suggestions: [] };
  return {
    ok: true,
    waited: true,
    suggestions: (session.extraction_suggestions || []).map((row) => ({
      ...row,
      status: row.status || "pending",
    })),
  };
}

function changedCategories(files, sessionId) {
  const out = {};
  (files || []).forEach((file) => {
    if (sessionId && file.session_id && file.session_id !== sessionId) return;
    if (file.user_category && file.user_category !== file.ai_category) {
      out[file.file_id] = file.user_category;
    }
  });
  return out;
}

async function runWithLimit(items, limit, worker) {
  const queue = items.slice();
  const width = Math.min(limit, queue.length);
  const runners = [];
  for (let i = 0; i < width; i += 1) {
    runners.push((async () => {
      while (queue.length) {
        const item = queue.shift();
        await worker(item);
      }
    })());
  }
  await Promise.all(runners);
}

function extractHeadline(batches, suggestionCount) {
  const total = (batches || []).length;
  if (total < 2) return "";
  const done = batches.filter((batch) => batch.status === "extracted");
  const reading = batches.find((batch) => batch.status === "extracting");
  const noun = suggestionCount === 1 ? "suggestion" : "suggestions";
  const parts = [];
  if (done.length) {
    const latest = done[done.length - 1];
    parts.push(`Batch ${latest.number} of ${total} read, ${suggestionCount} ${noun}.`);
  }
  if (reading) parts.push(`Reading batch ${reading.number} of ${total}...`);
  return parts.join(" ");
}

function importFileCount(batches, sessionFiles) {
  const failed = new Set(
    (batches || []).filter((batch) => batch.status === "failed").map((batch) => batch.number)
  );
  return (sessionFiles || []).filter((file) => !failed.has(file.batch_number)).length;
}

function FailedBatchButtons({ batches, onRetry }) {
  const failed = (batches || []).filter((batch) => batch.status === "failed");
  if (!failed.length) return null;
  return (
    <div className="mb-3 space-y-2">
      {failed.map((batch) => (
        <button
          key={batch.number}
          type="button"
          onClick={() => onRetry(batch.number)}
          className="text-sm font-medium text-blue-700 underline"
        >
          Retry batch {batch.number}
        </button>
      ))}
    </div>
  );
}

// ─── Step 1: Drop Zone ────────────────────────────────────────────────────

function DropZone({ onFilesSelected, disabled }) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const cameraRef = useRef(null);
  const isMobile = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    collectDroppedFiles(e.dataTransfer).then((dropped) => {
      if (dropped.length) onFilesSelected(dropped);
    }).catch(() => {
      toast.error("Could not read the dropped folder");
    });
  }, [disabled, onFilesSelected]);

  const handleBrowse = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleChange = useCallback((e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length) onFilesSelected(selected);
    e.target.value = "";
  }, [onFilesSelected]);

  const handleFolderChange = useCallback((e) => {
    e.stopPropagation();
    const selected = Array.from(e.target.files || []);
    if (selected.length) onFilesSelected(selected);
    e.target.value = "";
  }, [onFilesSelected]);

  return (
    <div
      data-bulk-dropzone=""
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleBrowse}
      className={`
        relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer
        transition-all duration-200
        ${isDragging
          ? "border-blue-500 bg-blue-50/60 scale-[1.01]"
          : "border-slate-300 hover:border-blue-400 hover:bg-slate-50/60"
        }
        ${disabled ? "opacity-50 pointer-events-none" : ""}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleChange}
        accept="image/*,.pdf,.eml,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.txt,.csv"
      />
      <input
        ref={folderRef}
        type="file"
        multiple
        className="hidden"
        accept="image/*,.pdf,.eml,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.txt,.csv"
        webkitdirectory=""
        directory=""
        onClick={(e) => e.stopPropagation()}
        onChange={handleFolderChange}
      />
      {isMobile && (
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleChange}
        />
      )}

      <div className="flex flex-col items-center gap-4">
        <div className={`
          w-16 h-16 rounded-2xl flex items-center justify-center
          ${isDragging ? "bg-blue-100" : "bg-slate-100"}
        `}>
          <Upload className={`h-8 w-8 ${isDragging ? "text-blue-600" : "text-slate-400"}`} />
        </div>

        <div>
          <p className="text-lg font-semibold text-slate-800">
            Drop everything here
          </p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Contracts, invoices, photos, permits, spec sheets, receipts — we'll sort it all.
            <br />
            Up to 500 files, 50 MB each. More than 100 are split into batches automatically. Images, PDFs, emails (.eml), Word docs, spreadsheets, CAD files.
          </p>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Button variant="outline" className="gap-2" type="button">
            <FolderOpen className="h-4 w-4" /> Browse Files
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) folderRef.current?.click();
            }}
          >
            <FolderOpen className="h-4 w-4" /> Choose folder
          </Button>
          {isMobile && (
            <Button
              variant="outline"
              className="gap-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              type="button"
              onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
            >
              <Camera className="h-4 w-4" /> Take Photo
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step 1: File List (pre-upload) ───────────────────────────────────────

function FileList({ files, uploading, onRemove }) {
  if (!files.length) return null;

  return (
    <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
      {files.map((f, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 bg-white rounded-xl border border-slate-200">
          {f.type?.startsWith("image/") ? (
            <ImageIcon className="h-4 w-4 text-indigo-500 shrink-0" />
          ) : (
            <File className="h-4 w-4 text-slate-400 shrink-0" />
          )}
          <span className="text-sm text-slate-700 truncate flex-1">{fileDisplayName(f)}</span>
          <span className="text-xs text-slate-400 shrink-0">{formatSize(f.size)}</span>
          {uploading ? (
            <Loader2 className="h-4 w-4 text-blue-500 animate-spin shrink-0" />
          ) : (
            <button
              onClick={() => onRemove(i)}
              className="p-1 text-slate-300 hover:text-red-500 transition-colors shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Step 2: Category Group (collapsible) ─────────────────────────────────

function CategoryGroup({ category, files, onUpdateFile, onPreview, onMoveFiles }) {
  const [expanded, setExpanded] = useState(true);
  const [selected, setSelected] = useState({});
  const [moveTo, setMoveTo] = useState("");
  const config = CATEGORY_MAP[category] || CATEGORY_MAP.other;
  const Icon = config.icon;
  const unsorted = category === "other";
  const label = unsorted ? "Unsorted" : config.label;

  const moveSelected = () => {
    const ids = files.filter((f) => selected[f.file_id]).map((f) => f.file_id);
    if (!moveTo || !ids.length || !onMoveFiles) return;
    onMoveFiles(ids, moveTo);
    setSelected({});
  };

  return (
    <div
      className="border border-slate-200 rounded-xl overflow-hidden"
      data-testid={unsorted ? "unsorted-group" : `category-group-${category}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className={`w-8 h-8 ${config.bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${config.fg}`} />
        </div>
        <span className="font-semibold text-slate-800 text-sm flex-1">
          {label}
        </span>
        <span className="text-xs text-slate-400 px-2 py-0.5 bg-white rounded-full border border-slate-200">
          {files.length} file{files.length !== 1 ? "s" : ""}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="divide-y divide-slate-100">
          {unsorted && (
            <div className="flex items-center gap-2 px-4 py-2 bg-white">
              <select
                data-testid="bulk-move-category"
                value={moveTo}
                onChange={(e) => setMoveTo(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600"
              >
                <option value="">Move to category</option>
                {CATEGORIES.filter((c) => c.value !== "other").map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid="bulk-move"
                onClick={moveSelected}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 text-white"
              >
                Move
              </button>
            </div>
          )}
          {files.map((f) => (
            <FileReviewRow
              key={f.file_id}
              file={f}
              onUpdate={onUpdateFile}
              onPreview={onPreview}
              selectable={unsorted}
              selected={!!selected[f.file_id]}
              onToggle={() => setSelected((prev) => ({ ...prev, [f.file_id]: !prev[f.file_id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 2: File Review Row ──────────────────────────────────────────────

function FileReviewRow({ file, onUpdate, onPreview, selectable, selected, onToggle }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(file.user_title || file.ai_title || file.filename);
  const conf = CONFIDENCE_CONFIG[file.ai_confidence] || CONFIDENCE_CONFIG.low;
  const ConfIcon = conf.icon;

  const currentCategory = file.user_category || file.ai_category || "other";
  const currentTitle = file.user_title || file.ai_title || file.filename;
  const isImage = file.file_type?.startsWith("image/");

  const handleTitleSave = () => {
    if (editTitle.trim() && editTitle !== currentTitle) {
      onUpdate(file.file_id, { user_title: editTitle.trim() });
    }
    setEditing(false);
  };

  const handleCategoryChange = (newCat) => {
    onUpdate(file.file_id, { user_category: newCat });
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50/50 transition-colors">
      {selectable && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggle}
          data-testid={`select-${file.file_id}`}
          aria-label={`Select ${file.filename}`}
        />
      )}
      <button
        type="button"
        onClick={() => onPreview && onPreview(file)}
        data-testid={`open-preview-${file.file_id}`}
        aria-label={`Preview ${file.filename}`}
        className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden"
      >
        {isImage && file.url ? (
          <img
            src={`${BACKEND_URL}${file.preview_url || file.url}`}
            alt=""
            className="w-10 h-10 object-cover rounded-lg"
          />
        ) : (
          <FileText className="h-4 w-4 text-slate-400" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
              onBlur={handleTitleSave}
              autoFocus
              className="text-sm border border-blue-300 rounded-lg px-2 py-1 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-800 truncate">
              {currentTitle}
            </span>
            <button
              onClick={() => setEditing(true)}
              className="p-0.5 text-slate-300 hover:text-blue-500 transition-colors shrink-0"
            >
              <Edit3 className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-400 truncate">{file.filename}</span>
          <span className="text-xs text-slate-300">&middot;</span>
          <span className="text-xs text-slate-400">{formatSize(file.size)}</span>
          <ConfIcon className={`h-3 w-3 ${conf.color} shrink-0`} title={conf.label} />
        </div>
        <p className="text-xs text-slate-500 mt-1" data-testid={`suggestion-copy-${file.file_id}`}>
          {suggestionCopy(currentCategory)}
        </p>
      </div>

      <select
        value={currentCategory}
        onChange={(e) => handleCategoryChange(e.target.value)}
        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-200 shrink-0"
      >
        {CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Step 3: Extraction Suggestion Card ──────────────────────────────────

function ExtractionSuggestionCard({ suggestion }) {
  const [showDetails, setShowDetails] = useState(false);
  const typeConfig = EXTRACTION_TYPE_CONFIG[suggestion.extraction_type] || EXTRACTION_TYPE_CONFIG.financial_entry;
  const TypeIcon = typeConfig.icon;
  const conf = CONFIDENCE_CONFIG[suggestion.confidence] || CONFIDENCE_CONFIG.medium;
  const ConfIcon = conf.icon;
  const isSkipped = suggestion.status === "skipped";

  return (
    <div className={`rounded-xl border transition-all duration-200 ${
      isSkipped ? "border-slate-200 bg-slate-50/50 opacity-60" : `${typeConfig.border} ${typeConfig.bg}`
    }`}>
      <div className="flex items-start gap-3 px-4 py-3">
        <div className={`w-9 h-9 ${typeConfig.iconBg} rounded-lg flex items-center justify-center shrink-0 mt-0.5`}>
          {isSkipped ? (
            <SkipForward className="h-4 w-4 text-slate-400" />
          ) : (
            <TypeIcon className={`h-4 w-4 ${typeConfig.fg}`} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              isSkipped ? "bg-slate-200 text-slate-500" : `${typeConfig.iconBg} ${typeConfig.fg}`
            }`}>
              {typeConfig.label}
            </span>
            <ConfIcon className={`h-3 w-3 ${isSkipped ? "text-slate-400" : conf.color}`} />
            <span className="text-xs text-slate-400 truncate">{suggestion.filename}</span>
          </div>

          <p className={`text-sm font-medium mt-1.5 ${isSkipped ? "text-slate-400 line-through" : "text-slate-800"}`}>
            {suggestion.summary}
          </p>
          {suggestion.data && !isSkipped && (
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 mt-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showDetails ? "Hide details" : "Show details"}
            </button>
          )}

          {showDetails && suggestion.data && (
            <div className="mt-2 text-xs bg-white/70 rounded-lg p-3 space-y-1 border border-slate-100">
              {Object.entries(suggestion.data).map(([key, value]) => {
                if (key === "type" || key === "line_items") return null;
                if (!value && value !== 0) return null;
                const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                const displayVal = typeof value === "number" && key.includes("amount")
                  ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                  : String(value);
                return (
                  <div key={key} className="flex items-start gap-2">
                    <span className="text-slate-400 shrink-0 w-28">{label}:</span>
                    <span className="text-slate-700 font-medium">{displayVal}</span>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function UploadReview({ appliedItems, cards, busy, onConfirm, onCancel, onSave, onAcceptAll, onSkipAll }) {
  const pending = (cards || []).filter((card) => card.status === "pending");
  const ready = pending.filter((card) => !cardNeedsChoice(card));
  if (!(appliedItems || []).length && !(cards || []).length) return null;
  return (
    <div className="space-y-3 mt-4">
      {(appliedItems || []).length > 0 && (
        <div data-testid="upload-applied">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Saved automatically</p>
          {appliedItems.map((item) => (
            <p key={`${item.documentId}-${item.field}`} className="text-sm text-slate-700" data-testid="upload-applied-row">
              {`Saved ${item.label} from ${item.filename}`}
            </p>
          ))}
        </div>
      )}
      {(cards || []).length > 0 && (
        <div data-testid="upload-cards">
          <p className="text-xs text-slate-500">{cardActionCopy()}</p>
          {pending.length > 0 && (
            <div className="flex items-center gap-2">
              {ready.length > 0 && (
                <Button type="button" variant="outline" size="sm" onClick={onAcceptAll} disabled={busy} data-testid="upload-accept-all">
                  <CheckCheck className="h-3.5 w-3.5" /> {`Accept all (${ready.length})`}
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" onClick={onSkipAll} disabled={busy} data-testid="upload-skip-all">
                <SkipForward className="h-3.5 w-3.5" /> Skip all
              </Button>
            </div>
          )}
          {cards.map((card) => (
            <AssistantConfirmCard
              key={card.actionId}
              action={{ ...card, editable: editableKeys(card.tool, card) }}
              busy={busy}
              onConfirm={onConfirm}
              onCancel={onCancel}
              onSave={onSave}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 4: Confirmation Summary (extended for Phase 2) ─────────────────

function ConfirmSummary({ categoryCounts, totalFiles, createdRecords, appliedCount, cardCount, already }) {
  const recordsByType = {};
  (createdRecords || []).forEach(r => {
    const t = r.record_type || "other";
    recordsByType[t] = (recordsByType[t] || 0) + 1;
  });

  const recordTypeLabels = {
    financial_entry: "Financial entries",
    contact: "Contacts",
    permit_enrichment: "Permits enriched",
    warranty_enrichment: "Warranties enriched",
    photo_link: "Photos linked",
  };

  const totalRecords = createdRecords?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 px-4 py-3 bg-green-50 rounded-xl border border-green-200">
        <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-green-800">
            {totalFiles} document{totalFiles !== 1 ? "s" : ""} imported
            {appliedCount != null && `, ${appliedCount} value${appliedCount === 1 ? "" : "s"} saved`}
            {cardCount != null && `, ${cardCount} card${cardCount === 1 ? "" : "s"}`}
            {totalRecords > 0 && `, ${totalRecords} record${totalRecords !== 1 ? "s" : ""} created`}
          </p>
          {already && (
            <p className="text-xs text-green-700 mt-0.5">Already imported.</p>
          )}
          <p className="text-xs text-green-600 mt-0.5">
            All files have been categorized and added to your project.
          </p>
        </div>
      </div>

      {/* Document category counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(categoryCounts).map(([cat, count]) => {
          const config = CATEGORY_MAP[cat] || CATEGORY_MAP.other;
          const Icon = config.icon;
          return (
            <div key={cat} className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl border border-slate-200">
              <div className={`w-7 h-7 ${config.bg} rounded-lg flex items-center justify-center`}>
                <Icon className={`h-3.5 w-3.5 ${config.fg}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{count}</p>
                <p className="text-xs text-slate-400">{config.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Structured records created (Phase 2) */}
      {totalRecords > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Records Created from AI Extraction
          </p>
          <div className="space-y-1.5">
            {Object.entries(recordsByType).map(([type, count]) => {
              const typeConf = EXTRACTION_TYPE_CONFIG[type] || {};
              const TypeIcon = typeConf.icon || FileSearch;
              return (
                <div key={type} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200">
                  <TypeIcon className={`h-4 w-4 ${typeConf.fg || "text-slate-500"}`} />
                  <span className="text-sm text-slate-700">
                    {count} {recordTypeLabels[type] || type}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Wizard Component ────────────────────────────────────────────────

export default function BulkUploadWizard({
  projectId, onClose, onComplete, initialSessionId, initialFiles, queueToken,
}) {
  // Step: 'upload' | 'review' | 'extract' | 'done'
  const [step, setStep] = useState("upload");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [skippedFiles, setSkippedFiles] = useState([]);
  const selectedCountRef = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionFiles, setSessionFiles] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [result, setResult] = useState(null);
  const [pendingCards, setPendingCards] = useState([]);
  const [cardsBusy, setCardsBusy] = useState(false);
  const pendingCardsRef = useRef([]);
  const cardInflight = useRef(new Set());
  pendingCardsRef.current = pendingCards;
  const [batches, setBatches] = useState([]);
  const [progressText, setProgressText] = useState("");
  const [finishedCount, setFinishedCount] = useState(0);
  const batchesRef = useRef([]);
  const sessionFilesRef = useRef([]);
  const cancelledRef = useRef(false);
  const timersRef = useRef(new Set());
  const suggestionsRef = useRef([]);
  const extractInflight = useRef(new Set());
  sessionFilesRef.current = sessionFiles;
  suggestionsRef.current = suggestions;

  useEffect(() => () => {
    cancelledRef.current = true;
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  useEffect(() => {
    if (initialSessionId) return;
    setPendingOffer(readBulkPending(projectId));
  }, [initialSessionId, projectId]);

  useEffect(() => {
    if (!(uploading || processing || extracting)) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading, processing, extracting]);

  const readOptions = useCallback((sessionForCategories) => ({
    categories: changedCategories(sessionFilesRef.current, sessionForCategories),
    cancelledRef,
    timersRef,
  }), []);

  const rememberBatches = useCallback((next) => {
    batchesRef.current = next;
    setBatches(next);
  }, []);

  const patchBatch = useCallback((number, patch) => {
    const next = batchesRef.current.map((item) => (
      item.number === number ? { ...item, ...patch } : item
    ));
    rememberBatches(next);
    setExtracting(next.some((item) => item.status === "extracting"));
  }, [rememberBatches]);

  const addSuggestions = useCallback((incoming) => {
    if (!incoming || !incoming.length) return;
    setSuggestions((prev) => {
      const seen = new Set();
      const next = [];
      (prev || []).concat(incoming).forEach((row) => {
        const id = row && row.suggestion_id;
        if (id && seen.has(id)) return;
        if (id) seen.add(id);
        next.push(row);
      });
      suggestionsRef.current = next;
      return next;
    });
  }, []);
  const [showPhoneCapture, setShowPhoneCapture] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [pendingOffer, setPendingOffer] = useState(() => (
    initialSessionId ? null : readBulkPending(projectId)
  ));
  const [closeAsked, setCloseAsked] = useState(false);

  // If opened with a pre-loaded session (from phone capture import),
  // jump straight to AI categorization → review
  useEffect(() => {
    if (!initialSessionId) return;
    let cancelled = false;
    (async () => {
      try {
        setUploading(true);
        setProcessing(true);
        const processRes = await api.post(
          `/projects/${projectId}/bulk-upload/${initialSessionId}/process`
        );
        if (cancelled) return;
        setSessionId(initialSessionId);
        setSessionFiles(processRes.data.files || []);
        setStep("review");
        toast.success("AI categorization complete — review the results below.");
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Processing failed");
        }
      } finally {
        if (!cancelled) {
          setUploading(false);
          setProcessing(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [initialSessionId, projectId]);

  // Step 1: Add files to queue. Junk, the wrong type, files over 50 MB,
  // and files past 500 are left out. Exact repeats in this drop are listed.
  const handleFilesSelected = useCallback((newFiles) => {
    const list = Array.isArray(newFiles) ? newFiles : Array.from(newFiles || []);
    const unique = dedupeQueued(list);
    const { accepted, skipped } = partitionUploadFiles(unique.files, selectedCountRef.current);
    const allSkipped = unique.skipped.concat(skipped);
    if (allSkipped.length) setSkippedFiles((prev) => prev.concat(allSkipped));
    if (!accepted.length) return;
    selectedCountRef.current += accepted.length;
    setSelectedFiles((prev) => prev.concat(accepted));
  }, []);

  const appliedQueue = useRef(null);
  useEffect(() => {
    if (!queueToken || appliedQueue.current === queueToken) return;
    appliedQueue.current = queueToken;
    if (initialFiles && initialFiles.length) handleFilesSelected(initialFiles);
  }, [queueToken, initialFiles, handleFilesSelected]);

  const handleRemoveFile = useCallback((index) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      selectedCountRef.current = next.length;
      return next;
    });
  }, []);

  const noteUploadError = (err) => {
    const errData = err.response?.data;
    const rejected = errData?.rejected || [];
    if (rejected.length > 0) {
      const names = rejected.map((row) => `${row.filename} (${row.reason})`).join(", ");
      toast.error(`All files rejected: ${names}`, { duration: 8000 });
    } else {
      toast.error((errData && errData.detail) || "Upload failed, please try again.");
    }
  };

  const runOneBatch = useCallback(async (batch, total) => {
    const show = total > 1;
    const prefix = `Batch ${batch.number} of ${total}`;
    try {
      if (!batch.sessionId) {
        if (show) flushSync(() => setProgressText(`${prefix}: uploading ${batch.files.length} files`));
        const formData = new FormData();
        batch.files.forEach((file) => formData.append("files", file));
        const uploadRes = await api.post(
          `/projects/${projectId}/bulk-upload`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 },
        );
        batch.sessionId = uploadRes.data.id;
        const saved = batchesRef.current.filter((item) => item.sessionId);
        writeBulkPending(projectId, {
          projectId,
          sessionIds: saved.map((item) => item.sessionId),
          batchSizes: saved.map((item) => (item.files || []).length),
          savedAt: new Date().toISOString(),
        });
        const rejected = uploadRes.data.rejected || [];
        if (rejected.length > 0) {
          const names = rejected.map((row) => `${row.filename} (${row.reason})`).join(", ");
          toast.error(`${rejected.length} file(s) rejected: ${names}`, { duration: 8000 });
        }
      }
      if (show) flushSync(() => setProgressText(`${prefix}: analyzing`));
      setProcessing(true);
      const processRes = await api.post(
        `/projects/${projectId}/bulk-upload/${batch.sessionId}/process`,
      );
      batch.status = "ready";
      batch.failedStep = null;
      if (show) flushSync(() => setProgressText(`${prefix}: ready`));
      return stampBatch(processRes.data.files, batch.sessionId, batch.number);
    } catch (err) {
      batch.status = "failed";
      batch.failedStep = batch.sessionId ? "process" : "upload";
      noteUploadError(err);
      return null;
    }
  }, [projectId]);

  const uploadGroups = useCallback(async (groups) => {
    setUploading(true);
    const items = groups.map((files, index) => ({
      number: index + 1,
      files,
      sessionId: null,
      status: "pending",
      failedStep: null,
    }));
    rememberBatches(items);
    setFinishedCount(0);
    const ready = [];
    for (let i = 0; i < items.length; i += 1) {
      const files = await runOneBatch(items[i], items.length);
      if (files) ready.push(...files);
      setFinishedCount(items.filter((item) => item.status === "ready").length);
      rememberBatches(items.map((item) => ({ ...item })));
    }
    setSessionFiles(ready);
    setSessionId(items.find((item) => item.sessionId)?.sessionId || null);
    if (ready.length) setStep("review");
    setUploading(false);
    setProcessing(false);
  }, [rememberBatches, runOneBatch]);

  // Step 1 → 2: Upload files to server, then process.
  // One batch uses the same two requests as before. More than one run in order.
  const handleUpload = useCallback(async () => {
    if (!selectedFiles.length) return;
    const groups = splitIntoBatches(selectedFiles, BULK_BATCH_SIZE);
    if (groups.length > 1) {
      await uploadGroups(groups);
      return;
    }
    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(f => formData.append("files", f));

      const uploadRes = await api.post(
        `/projects/${projectId}/bulk-upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }
      );

      const sid = uploadRes.data.id;
      setSessionId(sid);
      writeBulkPending(projectId, {
        projectId,
        sessionIds: [sid],
        batchSizes: [uploadRes.data.file_count || selectedFiles.length],
        savedAt: new Date().toISOString(),
      });

      // SEC-H5: show rejected files to the user
      const rejected = uploadRes.data.rejected || [];
      if (rejected.length > 0) {
        const names = rejected.map(r => `${r.filename} (${r.reason})`).join(", ");
        toast.error(`${rejected.length} file(s) rejected: ${names}`, { duration: 8000 });
      }

      toast.success(`${uploadRes.data.file_count} files uploaded, analyzing...`);

      // Immediately process
      setProcessing(true);
      const processRes = await api.post(
        `/projects/${projectId}/bulk-upload/${sid}/process`
      );

      setSessionFiles(processRes.data.files || []);
      setStep("review");
      setProcessing(false);
      toast.success("AI categorization complete — review the results below.");
    } catch (err) {
      console.error("Bulk upload error:", err);
      const errData = err.response?.data;
      const rejected = errData?.rejected || [];
      if (rejected.length > 0) {
        const names = rejected.map(r => `${r.filename} (${r.reason})`).join(", ");
        toast.error(`All files rejected: ${names}`, { duration: 8000 });
      } else {
        toast.error(errData?.detail || "Upload failed, please try again.");
      }
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  }, [selectedFiles, projectId, uploadGroups]);

  // Step 2: Update a file's user overrides
  const handleUpdateFile = useCallback((fileId, updates) => {
    setSessionFiles(prev =>
      prev.map(f => f.file_id === fileId ? { ...f, ...updates } : f)
    );
  }, []);

  // Same user_category field as the per-file select. Confirm stores it
  // through the CONTRACT-CAT-1 path when it differs from ai_category.
  const handleMoveFiles = useCallback((fileIds, category) => {
    const ids = new Set(fileIds);
    setSessionFiles((prev) => prev.map((f) => (
      ids.has(f.file_id) ? { ...f, user_category: category } : f
    )));
  }, []);

  const confirmListed = useCallback(async (activeSuggestions, onlyNumber) => {
    setConfirming(true);
    const items = batchesRef.current.map((item) => ({ ...item }));
    let docs = 0;
    let applied = 0;
    let cards = 0;
    let photos = 0;
    const records = [];
    const counts = {};
    const appliedRows = [];
    const cardIds = [];
    const created = [];
    let anyDone = false;
    try {
      for (let i = 0; i < items.length; i += 1) {
        const batch = items[i];
        if (onlyNumber && batch.number !== onlyNumber) continue;
        if (!batch.sessionId || batch.status === "failed" || batch.status === "done") continue;
        const own = sessionFilesRef.current.filter((file) => file.session_id === batch.sessionId);
        const confirmedFiles = buildConfirmedFiles(own, activeSuggestions);
        if (!confirmedFiles.length) continue;
        try {
          const res = await api.post(
            `/projects/${projectId}/bulk-upload/${batch.sessionId}/confirm`,
            confirmedFiles,
          );
          batch.status = "done";
          anyDone = true;
          docs += (res.data.created || []).length;
          applied += (res.data.applied || []).length;
          cards += (res.data.card_ids || []).length;
          photos += res.data.photos_not_read || 0;
          (res.data.applied || []).forEach((row) => appliedRows.push(row));
          (res.data.card_ids || []).forEach((id) => cardIds.push(id));
          (res.data.created || []).forEach((row) => created.push(row));
          (res.data.created_records || []).forEach((row) => records.push(row));
          confirmedFiles.forEach((file) => {
            counts[file.category] = (counts[file.category] || 0) + 1;
          });
        } catch (err) {
          const detail = err.response?.data?.detail;
          const already = err.response?.status === 409
            && typeof detail === "string"
            && detail.indexOf("Already imported") !== -1;
          if (already) {
            batch.status = "done";
            anyDone = true;
            continue;
          }
          batch.status = "failed";
          batch.failedStep = "confirm";
          if (err.response?.status === 409 && typeof detail === "string" && detail.indexOf("Still extracting") !== -1) {
            toast.error(detail);
          } else {
            toast.error(detail || "Import failed, please try again.");
          }
        }
      }
    } finally {
      rememberBatches(items);
      setConfirming(false);
    }
    if (!anyDone) return;
    const review = reviewFromConfirm(
      { applied: appliedRows, card_ids: cardIds, created },
      sessionFilesRef.current,
    );
    setPendingCards(review.cards);
    setResult({
      totalFiles: docs,
      categoryCounts: counts,
      createdRecords: records,
      appliedItems: review.applied,
      appliedCount: applied,
      cardCount: cards,
    });
    setStep("done");
    const open = items.filter((item) => item.sessionId);
    if (open.length && open.every((item) => item.status === "done")) clearBulkPending(projectId);
    if (docs > 0) {
      const recordCount = records.length;
      toast.success(recordCount > 0
        ? `${docs} documents + ${recordCount} records imported!`
        : `${docs} documents imported!`);
      if (photos > 0) {
        toast.warning(`${photos} photo${photos === 1 ? " was" : "s were"} stored but not read (the limit is 30 photos per upload). Upload them again on their own or in chat to have them read.`);
      }
      if (onComplete) onComplete();
    }
  }, [onComplete, projectId, rememberBatches]);

  const extractAll = useCallback(async () => {
    if (cancelledRef.current) return;
    setStep("extract");
    const starting = batchesRef.current.map((item) => (
      !item.sessionId || item.status === "failed"
        ? item
        : { ...item, status: "extracting", failedStep: null }
    ));
    rememberBatches(starting);
    setExtracting(starting.some((item) => item.status === "extracting"));
    const eligible = starting.filter((item) => item.status === "extracting");
    let addedNow = 0;
    await runWithLimit(eligible, EXTRACT_AT_ONCE, async (batch) => {
      if (cancelledRef.current) return;
      let got = { ok: false };
      try {
        got = await loadBatchSuggestions(
          projectId, batch.sessionId, null, readOptions(batch.sessionId),
        );
      } catch (err) {
        got = { ok: false };
      }
      if (cancelledRef.current || got.cancelled) return;
      if (!got.ok) {
        patchBatch(batch.number, { status: "failed", failedStep: "extract" });
        return;
      }
      patchBatch(batch.number, { status: "extracted", failedStep: null });
      addedNow += (got.suggestions || []).length;
      addSuggestions(got.suggestions);
    });
    if (cancelledRef.current) return;
    const latest = batchesRef.current;
    setExtracting(latest.some((item) => item.status === "extracting"));
    const anyFailed = latest.some((item) => item.status === "failed");
    const known = addedNow + suggestionsRef.current.length;
    if (!known && !anyFailed) {
      await confirmListed([]);
      return;
    }
    if (!known && anyFailed) {
      toast.info("AI extraction unavailable, importing documents only.");
    }
    setStep("extract");
  }, [addSuggestions, confirmListed, patchBatch, projectId, readOptions, rememberBatches]);

  const retryBatch = useCallback(async (number) => {
    const items = batchesRef.current.map((item) => ({ ...item }));
    const batch = items.find((item) => item.number === number);
    if (!batch) return;
    if (batch.failedStep === "confirm") {
      batch.status = "extracted";
      batch.failedStep = null;
      rememberBatches(items);
      await confirmListed(suggestions, number);
      return;
    }
    if (batch.failedStep === "extract") {
      if (extractInflight.current.has(number) || batch.status === "extracting") return;
      extractInflight.current.add(number);
      patchBatch(number, { status: "extracting", failedStep: "extract" });
      try {
        const got = await loadBatchSuggestions(
          projectId, batch.sessionId, () => setStep("extract"), readOptions(batch.sessionId),
        );
        if (got.ok) {
          patchBatch(number, { status: "extracted", failedStep: null });
          addSuggestions(got.suggestions);
        } else {
          patchBatch(number, { status: "failed", failedStep: "extract" });
        }
      } catch (err) {
        patchBatch(number, { status: "failed", failedStep: "extract" });
      } finally {
        extractInflight.current.delete(number);
      }
      return;
    }
    if (batch.failedStep === "upload") batch.sessionId = null;
    setUploading(true);
    const files = await runOneBatch(batch, items.length);
    if (files) {
      setSessionFiles((prev) => prev.filter((file) => file.batch_number !== number).concat(files));
      setStep("review");
    }
    rememberBatches(items.map((item) => ({ ...item })));
    setFinishedCount(items.filter((item) => item.status === "ready" || item.status === "extracted" || item.status === "done").length);
    setUploading(false);
    setProcessing(false);
  }, [addSuggestions, confirmListed, patchBatch, projectId, readOptions, rememberBatches, runOneBatch, suggestions]);

  // Step 3 → 4 (or Step 2 → 4): Confirm & import
  // DOC-1: declared before handleProceedToExtract so that callback can list it
  // in its deps and never call a stale handleConfirm with empty sessionFiles.
  const handleConfirm = useCallback(async (overrideSuggestions) => {
    if (batchesRef.current.length > 1) {
      await confirmListed(overrideSuggestions || suggestions);
      return;
    }
    setConfirming(true);

    try {
      // Build confirmed files with extraction data attached (DOC-1: shared helper)
      const activeSuggestions = overrideSuggestions || suggestions;
      const confirmedFiles = buildConfirmedFiles(sessionFiles, activeSuggestions);

      // DOC-1: never post an empty import
      if (confirmedFiles.length === 0) {
        toast.error("No files to import. Please upload the files again.");
        return;
      }

      const res = await api.post(
        `/projects/${projectId}/bulk-upload/${sessionId}/confirm`,
        confirmedFiles
      );

      // Build category counts for summary
      const counts = {};
      confirmedFiles.forEach(f => {
        counts[f.category] = (counts[f.category] || 0) + 1;
      });

      const review = reviewFromConfirm(res.data, sessionFiles);
      setPendingCards(review.cards);
      setResult({
        totalFiles: res.data.created?.length || 0,
        categoryCounts: counts,
        createdRecords: res.data.created_records || [],
        appliedItems: review.applied,
        appliedCount: Array.isArray(res.data.applied) ? res.data.applied.length : null,
        cardCount: Array.isArray(res.data.card_ids) ? res.data.card_ids.length : null,
      });
      setStep("done");
      clearBulkPending(projectId);

      const docCount = res.data.created?.length || 0;
      const recordCount = res.data.created_records?.length || 0;
      if (docCount === 0) {
        // DOC-1: 0 documents is a failure, not a success
        toast.error("No documents were imported. Please try again.");
      } else {
        const msg = recordCount > 0
          ? `${docCount} documents + ${recordCount} records imported!`
          : `${docCount} documents imported!`;
        toast.success(msg);
        // STAB-1B: photos over the read cap are stored but not read
        const n = res.data.photos_not_read || 0;
        if (n > 0) {
          toast.warning(`${n} photo${n === 1 ? " was" : "s were"} stored but not read (the limit is 30 photos per upload). Upload them again on their own or in chat to have them read.`);
        }
      }

      if (onComplete) onComplete();
    } catch (err) {
      console.error("Confirm error:", err);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "string" && detail.indexOf("Already imported") !== -1) {
        clearBulkPending(projectId);
        setPendingCards([]);
        setResult({
          totalFiles: 0,
          categoryCounts: {},
          createdRecords: [],
          appliedItems: [],
          already: true,
        });
        setStep("done");
        return;
      }
      if (err.response?.status === 409 && typeof detail === "string" && detail.indexOf("Still extracting") !== -1) {
        toast.error(detail);
        return;
      }
      toast.error(detail || "Import failed, please try again.");
    } finally {
      setConfirming(false);
    }
  }, [confirmListed, sessionFiles, suggestions, projectId, sessionId, onComplete]);

  // Step 2 → 3: Run AI extraction, then show suggestions
  const handleProceedToExtract = useCallback(async () => {
    if (batchesRef.current.length > 1) {
      await extractAll();
      return;
    }
    setExtracting(true);

    const showUnavailable = () => {
      toast.info("AI extraction unavailable, importing documents only.");
      setSuggestions([]);
      setStep("extract");
    };

    try {
      const got = await loadBatchSuggestions(
        projectId, sessionId, () => setStep("extract"), readOptions(sessionId),
      );
      if (!got.ok) {
        showUnavailable();
        return;
      }
      setSuggestions(got.suggestions);
      if (got.waited || got.suggestions.length > 0) {
        setStep("extract");
        if (got.suggestions.length > 0) {
          const count = got.suggestions.length;
          toast.success(`AI found ${count} extraction suggestion${count !== 1 ? "s" : ""}. Review below.`);
        }
        return;
      }
      await handleConfirm([]);
    } catch (err) {
      console.error("Extraction error:", err);
      showUnavailable();
    } finally {
      setExtracting(false);
    }
  }, [extractAll, projectId, readOptions, sessionId, handleConfirm]);

  const saveCardEdits = useCallback((actionId, draft) => {
    const edits = {};
    Object.entries(draft || {}).forEach(([key, text]) => {
      const number = Number(text);
      edits[key] = text !== "" && Number.isFinite(number) && String(number) === String(text).trim() ? number : text;
    });
    setPendingCards((prev) => {
      const next = prev.map((row) => (row.actionId === actionId ? { ...row, edits } : row));
      pendingCardsRef.current = next;
      return next;
    });
  }, []);

  const postCard = useCallback(async (actionId, confirm, choice) => {
    if (!actionId || cardInflight.current.has(actionId)) return false;
    const card = pendingCardsRef.current.find((row) => row.actionId === actionId);
    if (!card || card.status !== "pending") return false;
    cardInflight.current.add(actionId);
    setCardsBusy(true);
    const body = { action_id: actionId, confirm: Boolean(confirm) };
    if (choice) body.choice = choice;
    if (confirm && card.edits) body.arguments = card.edits;
    try {
      const { data } = await api.post("/agent/confirm-action", body);
      if (data && data.result === "possible_duplicate" && data.changed === false) {
        setPendingCards((prev) => {
          const next = prev.map((row) => (
            row.actionId === actionId
              ? { ...row, status: "pending", result: "possible_duplicate", possibleDuplicate: data.existing || null }
              : row
          ));
          pendingCardsRef.current = next;
          return next;
        });
        return false;
      }
      if (data && data.result === "use_existing") {
        setPendingCards((prev) => {
          const next = prev.map((row) => (
            row.actionId === actionId ? { ...row, status: "skipped", result: "use_existing" } : row
          ));
          pendingCardsRef.current = next;
          return next;
        });
        return true;
      }
      if (!confirm || (data && data.changed === true)) {
        setPendingCards((prev) => {
          const next = prev.map((row) => (
            row.actionId === actionId ? { ...row, status: confirm ? "accepted" : "skipped", result: data && data.result } : row
          ));
          pendingCardsRef.current = next;
          return next;
        });
        return true;
      }
      toast.error((data && data.message) || "That card was not saved.");
      return false;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update that card.");
      return false;
    } finally {
      cardInflight.current.delete(actionId);
      setCardsBusy(false);
    }
  }, []);

  const acceptAllCards = useCallback(async () => {
    const ids = pendingCardsRef.current
      .filter((card) => card.status === "pending" && !cardNeedsChoice(card))
      .map((card) => card.actionId);
    for (let i = 0; i < ids.length; i += 1) {
      const ok = await postCard(ids[i], true);
      if (!ok) return;
    }
  }, [postCard]);

  const skipAllCards = useCallback(async () => {
    const ids = pendingCardsRef.current
      .filter((card) => card.status === "pending")
      .map((card) => card.actionId);
    for (let i = 0; i < ids.length; i += 1) {
      const ok = await postCard(ids[i], false);
      if (!ok) return;
    }
  }, [postCard]);

  // Phone capture → import callback
  const handlePhoneCaptureImport = useCallback(async ({ sessionId: bulkSid, fileCount }) => {
    setShowPhoneCapture(false);
    setUploading(true);
    setProcessing(true);

    try {
      const processRes = await api.post(
        `/projects/${projectId}/bulk-upload/${bulkSid}/process`
      );
      setSessionId(bulkSid);
      setSessionFiles(processRes.data.files || []);
      setStep("review");
      toast.success("AI categorization complete — review the results below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Processing failed");
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  }, [projectId]);

  const uploadOpen = step !== "done" && (
    uploading || processing || Boolean(sessionId)
    || batches.some((batch) => batch.sessionId && batch.status !== "done")
  );
  const requestClose = useCallback(() => {
    if (uploadOpen) {
      setCloseAsked(true);
      return;
    }
    onClose();
  }, [onClose, uploadOpen]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const resumePending = useCallback(async () => {
    const pending = readBulkPending(projectId);
    if (!pending) return;
    const sessions = [];
    const keptIds = [];
    const keptSizes = [];
    for (let i = 0; i < pending.sessionIds.length; i += 1) {
      const sid = pending.sessionIds[i];
      try {
        const res = await api.get(`/projects/${projectId}/bulk-upload/${sid}`);
        sessions.push(res.data);
        keptIds.push(sid);
        keptSizes.push(pending.batchSizes[i] || ((res.data && res.data.files) || []).length);
      } catch (err) {
        if (!(err.response && err.response.status === 404)) {
          toast.error("Could not resume this upload");
          return;
        }
      }
    }
    if (!keptIds.length) {
      clearBulkPending(projectId);
      setPendingOffer(null);
      return;
    }
    if (keptIds.length !== pending.sessionIds.length) {
      writeBulkPending(projectId, {
        projectId,
        sessionIds: keptIds,
        batchSizes: keptSizes,
        savedAt: pending.savedAt,
      });
    }
    const nextBatches = [];
    const files = [];
    const nextSuggestions = [];
    sessions.forEach((session, index) => {
      const status = session.status === "extract_failed"
        ? "failed"
        : (session.status === "extracting" ? "extracting" : (session.status === "extracted" ? "extracted" : "ready"));
      nextBatches.push({
        number: index + 1,
        files: [],
        size: (session.files || []).length,
        sessionId: session.id,
        status,
        failedStep: status === "failed" ? "extract" : null,
      });
      (session.files || []).forEach((file) => {
        files.push({ ...file, session_id: session.id, batch_number: index + 1 });
      });
      if (status === "extracted") {
        (session.extraction_suggestions || []).forEach((row) => {
          nextSuggestions.push({ ...row, status: row.status || "pending" });
        });
      }
    });
    rememberBatches(nextBatches);
    setSessionFiles(files);
    setSuggestions(nextSuggestions);
    setSessionId(sessions[0].id || keptIds[0]);
    setPendingOffer(null);
    setCloseAsked(false);
    const reading = nextBatches.filter((batch) => batch.status === "extracting");
    if (!reading.length) {
      setStep(nextSuggestions.length ? "extract" : "review");
      return;
    }
    setStep("extract");
    setExtracting(true);
    const collected = nextSuggestions.slice();
    await runWithLimit(reading, EXTRACT_AT_ONCE, async (batch) => {
      if (cancelledRef.current) return;
      const got = await loadBatchSuggestions(
        projectId, batch.sessionId, null, { ...readOptions(batch.sessionId), skipPost: true },
      );
      if (cancelledRef.current || !got || got.cancelled) return;
      if (!got.ok) {
        batch.status = "failed";
        batch.failedStep = "extract";
      } else {
        batch.status = "extracted";
        got.suggestions.forEach((row) => collected.push(row));
        setSuggestions(collected.slice());
      }
      rememberBatches(nextBatches.map((item) => ({ ...item })));
    });
    if (cancelledRef.current) return;
    setExtracting(false);
    setStep("extract");
  }, [projectId, readOptions, rememberBatches]);

  // Group files by category for review step
  const groupedFiles = React.useMemo(() => groupSessionFiles(sessionFiles), [sessionFiles]);
  const importCount = batches.length > 1 ? importFileCount(batches, sessionFiles) : null;
  const importLabel = importCount === null ? "" : ` ${importCount} file${importCount === 1 ? "" : "s"}`;

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
    {showPhoneCapture && (
      <PhoneCaptureModal
        projectId={projectId}
        onClose={() => setShowPhoneCapture(false)}
        onImport={handlePhoneCaptureImport}
      />
    )}
    {previewFile && (
      <UploadPreview
        file={previewFile}
        projectId={projectId}
        sessionId={previewFile.session_id || sessionId}
        onClose={() => setPreviewFile(null)}
      />
    )}
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            {(step === "review" || step === "extract") && (
              <button
                onClick={() => {
                  if (step === "extract") {
                    setStep("review");
                    setSuggestions([]);
                  } else {
                    setStep("upload");
                    setSessionFiles([]);
                    setSessionId(null);
                    rememberBatches([]);
                    setProgressText("");
                    setFinishedCount(0);
                  }
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-white/60 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {step === "upload" && "Bulk Upload"}
                {step === "review" && "Review & Categorize"}
                {step === "extract" && "AI Extraction Review"}
                {step === "done" && "Import Complete"}
              </h2>
              <p className="text-xs text-slate-500">
                {step === "upload" && "Drop all your files — we'll organize them with AI"}
                {step === "review" && `${sessionFiles.length} files categorized — review before extracting data`}
                {step === "extract" && (
                  extractHeadline(batches, suggestions.length)
                  || `${suggestions.length} suggestion${suggestions.length !== 1 ? "s" : ""}. Edit a value before import.`
                )}
                {step === "done" && "All files have been imported to your project"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-white/60 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 bg-slate-50 border-b border-slate-100">
          {STEP_CONFIG.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                step === s.key
                  ? "bg-blue-100 text-blue-700"
                  : (STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(s.key)
                      ? "bg-green-100 text-green-700"
                      : "text-slate-400")
              }`}>
                {s.label}
              </span>
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {closeAsked && (
            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4" role="alertdialog">
              <p className="text-sm text-slate-700">Your upload is not imported yet. Close anyway? You can resume it later from this project.</p>
              <div className="mt-3 flex gap-2">
                <Button type="button" variant="outline" onClick={() => setCloseAsked(false)}>Keep working</Button>
                <Button type="button" onClick={onClose}>Close</Button>
              </div>
            </div>
          )}
          {/* Step 1: Upload */}
          {step === "upload" && (
            <>
              {pendingOffer && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-sm text-slate-700">{`You have an unfinished upload (${pendingFileCount(pendingOffer)} files). Resume or Discard.`}</p>
                  <div className="mt-3 flex gap-2">
                    <Button type="button" onClick={resumePending}>Resume</Button>
                    <Button type="button" variant="outline" onClick={() => { clearBulkPending(projectId); setPendingOffer(null); }}>Discard</Button>
                  </div>
                </div>
              )}
              <DropZone
                onFilesSelected={handleFilesSelected}
                disabled={uploading || processing}
              />

              <div className="mt-4 flex items-center justify-center">
                <button
                  onClick={() => setShowPhoneCapture(true)}
                  disabled={uploading || processing}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Smartphone className="h-4 w-4" />
                  Snap from Phone
                  <span className="text-xs text-indigo-400 font-normal">— scan QR, snap photos, auto-sync here</span>
                </button>
              </div>

              {progressText && (
                <div className="mt-4" data-testid="batch-progress">
                  <p className="text-sm text-slate-600">{progressText}</p>
                  <div className="mt-2 h-2 rounded bg-slate-200" aria-hidden="true">
                    <div
                      className="h-2 rounded bg-blue-600"
                      style={{ width: `${batches.length ? Math.round((finishedCount / batches.length) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <FileList
                files={selectedFiles}
                uploading={uploading || processing}
                onRemove={handleRemoveFile}
              />
              {(selectedFiles.length > 0 || skippedFiles.length > 0) && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="upload-queue-summary">
                  <p className="text-sm text-slate-600">
                    {selectedFiles.length} accepted, {skippedFiles.length} skipped
                  </p>
                  {skippedFiles.length > 0 && (
                    <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                      {skippedFiles.map((item, index) => (
                        <li key={`${item.name}-${item.reason}-${index}`} className="text-xs text-slate-500">
                          {item.name} ({item.reason})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {selectedFiles.length > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} selected
                    ({formatSize(selectedFiles.reduce((acc, f) => acc + f.size, 0))})
                  </p>
                  <Button
                    onClick={handleUpload}
                    disabled={uploading || processing}
                    className="bg-blue-600 hover:bg-blue-500 text-white gap-2"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                    ) : processing ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Upload className="h-4 w-4" /> Upload & Categorize</>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Step 2: Review Categories */}
          {step === "review" && (
            <div className="space-y-4">
              <FailedBatchButtons batches={batches} onRetry={retryBatch} />
              {batches.length > 1 ? batches.map((batch) => (
                <div key={batch.number}>
                  <p className="text-xs font-medium text-slate-500 mb-2">Batch {batch.number}</p>
                  <div className="space-y-4">
                    {Object.entries(groupSessionFiles(sessionFiles.filter((file) => file.batch_number === batch.number))).map(([category, files]) => (
                      <CategoryGroup
                        key={`${batch.number}-${category}`}
                        category={category}
                        files={files}
                        onUpdateFile={handleUpdateFile}
                        onPreview={setPreviewFile}
                        onMoveFiles={handleMoveFiles}
                      />
                    ))}
                  </div>
                </div>
              )) : Object.entries(groupedFiles).map(([category, files]) => (
                <CategoryGroup
                  key={category}
                  category={category}
                  files={files}
                  onUpdateFile={handleUpdateFile}
                  onPreview={setPreviewFile}
                  onMoveFiles={handleMoveFiles}
                />
              ))}

              {sessionFiles.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No files to review</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Extraction Review (Phase 2) */}
          {step === "extract" && (
            <div className="space-y-3">
              {batches.filter((batch) => batch.status === "failed" && batch.failedStep === "extract").map((batch) => (
                <p key={`read-${batch.number}`} className="text-sm text-slate-700">
                  {`We could not finish reading batch ${batch.number} of ${batches.length}. Retry, or import the rest now.`}
                </p>
              ))}
              <FailedBatchButtons batches={batches} onRetry={retryBatch} />
              {extracting && batches.length < 2 && (
                <p className="text-sm text-slate-600">AI is still reading your files...</p>
              )}
              {suggestions.map((s) => (
                <ExtractionSuggestionCard key={s.suggestion_id} suggestion={s} />
              ))}

              {suggestions.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <FileSearch className="h-8 w-8 mx-auto mb-2" />
                  <p>No extraction suggestions</p>
                  <p className="text-xs mt-1">AI didn't find structured data to extract from these files.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Done */}
          {step === "done" && result && (
            <>
              <FailedBatchButtons batches={batches} onRetry={retryBatch} />
              <ConfirmSummary
                categoryCounts={result.categoryCounts}
                totalFiles={result.totalFiles}
                createdRecords={result.createdRecords}
                appliedCount={result.appliedCount}
                cardCount={result.cardCount}
                already={result.already}
              />
              <UploadReview
                appliedItems={result.appliedItems}
                cards={pendingCards}
                busy={cardsBusy}
                onConfirm={(id, choice) => postCard(id, true, choice)}
                onCancel={(id) => postCard(id, false)}
                onSave={saveCardEdits}
                onAcceptAll={acceptAllCards}
                onSkipAll={skipAllCards}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          {step === "review" && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStep("upload");
                  setSessionFiles([]);
                  setSessionId(null);
                  rememberBatches([]);
                  setProgressText("");
                  setFinishedCount(0);
                }}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" /> Start Over
              </Button>
              <Button
                onClick={handleProceedToExtract}
                disabled={extracting || !sessionFiles.length}
                className="bg-blue-600 hover:bg-blue-500 text-white gap-2"
              >
                {extracting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Extracting data...</>
                ) : (
                  <><Sparkles className="h-4 w-4" /> Extract Data & Continue</>
                )}
              </Button>
            </>
          )}
          {step === "extract" && (
            <>
              <Button
                variant="outline"
                onClick={() => { setStep("review"); setSuggestions([]); }}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Review
              </Button>
              <Button
                onClick={() => handleConfirm()}
                disabled={confirming || extracting || batches.some((batch) => batch.status === "extracting")}
                className="bg-blue-600 hover:bg-blue-500 text-white gap-2"
              >
                {confirming ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Importing...</>
                ) : (
                  <><Check className="h-4 w-4" /> Confirm & Import{importLabel}</>
                )}
              </Button>
            </>
          )}
          {step === "done" && (
            <div className="ml-auto">
              <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-500 text-white gap-2">
                <Check className="h-4 w-4" /> Done
              </Button>
            </div>
          )}
          {step === "upload" && (
            <div className="ml-auto">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

const STEP_ORDER = ["upload", "review", "extract", "done"];
const STEP_CONFIG = [
  { key: "upload",  label: "Upload" },
  { key: "review",  label: "Categorize" },
  { key: "extract", label: "Extract" },
  { key: "done",    label: "Done" },
];

// Re-export PhoneCaptureModal for standalone use
export { PhoneCaptureModal };
